import { EventEmitter, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  concat,
  fromEvent,
  Observable,
  Subject,
  takeUntil,
  timer,
} from 'rxjs';
import * as cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';
import * as larvitar from 'larvitar';
import { v4 as uuidv4 } from 'uuid';
import pako from 'pako';

import {
  DEFAULT_STYLE,
  IMAGE_FILE_NAME,
  ISerieData,
  IUndoRedoEvent,
  IViewersObjectBoolean,
  IViewersObjectNumber,
  MASK_FILE_NAME,
  OptionsForTool,
  TYPE_OF_CHANGE_RESULT,
} from './cornerstone.data';
import Utils from '@shared/utils';
import { IVector } from '@shared/models';
import { TypedArray } from '@shared/data';
import { StateManagerProjectService } from '@state-manager';
import {
  EraseSquareBrushTool,
  EraseCircleBrushTool,
  SquareBrushTool,
  CircleBrushTool,
  CustomPolylineFreehandTool,
  ShowDiffTool,
  CustomZoomMouseWheelTool,
  EmptyTool,
  StackScrollMouseWheelTool,
} from './custom-tools';
import { SegmentationService } from './custom-tools/segmentation/segmentation.service';
import {
  CornerstoneTool,
  INrrdFileHeader,
  NRRD_TYPES_TO_TYPEDARRAY,
  CornerstoneMouseButton,
  CornerstoneAdditionalTool,
  SegmentationType,
  ViewWindowIndex,
  BoneModelType,
  NRRD_TYPES_TO_TYPED_ARRAY,
  ARRAY_NRRD_TYPES,
  BYTES_INDEX,
} from './data';
import { IColorData } from './colors-menu/colors.data';
import {
  DataRequestService,
  PixelDataTransformationService,
  ProjectActionsRequestService,
  ReferenceLineService,
} from './services';
import { UndoRedoService } from './custom-tools/undo-redo.service';
import { ViewerService } from './viewer.service';
import { ConfirmDialogService } from '@shared/confirm-dialog';
import { TextsForErrorsDialogs } from '@shared/confirm-dialog/confirm-dialog.data';
import {
  EventOutcomeLogType,
  EventTypeLogType,
} from './services/project-actions.data';
import { LogMessageAction } from './services/log-event.data';
import { AlertDialogService } from '@shared/alert-dialog';
import { Router } from '@angular/router';

const FileSaver = require('file-saver');
const segModule = cornerstoneTools.getModule('segmentation');
const external = cornerstoneTools.external;
const { getters, setters } = segModule;

@Injectable()
export class CornerstoneService {
  public activeLabel = 1;
  public changedSlicesForUpdateInfo: number[] = [];
  public resetViewsEmitter: EventEmitter<boolean> = new EventEmitter(false);
  public resetChangesEmitter: EventEmitter<boolean> = new EventEmitter(false);

  private _basedFullPixelData = new Map<string, TypedArray>();
  private _changedFullPixelData = new Map<string, TypedArray>();
  private _nrrdFileOfMask?: Blob;
  private _maskHeader?: INrrdFileHeader;
  private _maskCustomOrder?: boolean;
  private _viewerElements: { [key: string]: HTMLElement } = {};
  private _unsubscribe$ = new Subject();
  private _breakViewOfSlice$ = new Subject();
  private _maskOpacity$: BehaviorSubject<number> = new BehaviorSubject<number>(
    51
  );

  private _mapOfRenderedSlicesForShowChanges: Map<ViewWindowIndex, number[]> =
    new Map<ViewWindowIndex, number[]>();

  private _mapOfViewedSlices: Map<number, boolean[]> = new Map<
    number,
    boolean[]
  >();

  private _isStartLoadOfMaskForViewFinish: IViewersObjectBoolean = {
    viewer1: false,
    viewer2: false,
    viewer3: false,
  };

  private _undoBeforeShowDiff: any = {
    viewer1: [],
    viewer2: [],
    viewer3: [],
  };
  private _redoBeforeShowDiff: any = {
    viewer1: [],
    viewer2: [],
    viewer3: [],
  };

  private _viewSizes: Record<SegmentationType, number[]> = {
    axial: [0, 0, 0],
    coronal: [0, 0, 0],
    sagittal: [0, 0, 0],
  };

  private _isResetCalled = false;
  private _logsForEditEvents = new Map<string, number[]>();

  private closeOpenedMenu = new Subject<void>();
  closeOpenedMenu$ = this.closeOpenedMenu.asObservable();

  constructor(
    private _stateManagerProjectService: StateManagerProjectService,
    private _undoRedoService: UndoRedoService,
    private _segmentationService: SegmentationService,
    private _referenceLineService: ReferenceLineService,
    private _pixelDataTransformationService: PixelDataTransformationService,
    private _dataRequestService: DataRequestService,
    private _viewerService: ViewerService,
    private _confirmDialog: ConfirmDialogService,
    private _projectActionsRequestService: ProjectActionsRequestService,
    private alertDialogService: AlertDialogService,
    private router: Router
  ) {
    this._stateManagerProjectService.currentActiveColor = this.activeLabel;

    for (let i = 1; i < 4; i++) {
      this._mapOfViewedSlices.set(i, []);
      this._logsForEditEvents.set(this._getViewName(i), []);
    }
  }

  getViewerElements(): { [key: string]: HTMLElement } {
    return this._viewerElements;
  }

  init(): void {
    Array.from(Array(3)).forEach((_, index) => {
      const i = index + 1;
      this._viewerElements[i] = document.getElementById(this._getViewerId(i))!;
    });

    this._referenceLineService.init(this._viewerElements);

    this._undoRedoService.undoRedoEmitter
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((value: IUndoRedoEvent) => {
        if (value) {
          const element = document.getElementById(value.viewerId);
          const numberOfFrames = cornerstoneTools.getToolState(element, 'stack')
            .data[0].imageIds.length;
          const slicelength =
            this._changedFullPixelData.get(value.viewerId)!.length /
            numberOfFrames;

          const pixelData = this._changedFullPixelData.get(value.viewerId)!;
          for (let i = 0; i < value.actions.length - 1; i++) {
            pixelData[
              value.actions[i].sliceIndex * slicelength +
              value.actions[i].pixelIndex
            ] = value.actions[i].color;
          }
        }
      });
  }

  /**
   * init larvitar library
   * @function initByLarvitar
   */
  initByLarvitar(): void {
    larvitar.initLarvitarStore();
    larvitar.initializeCSTools();
    larvitar.larvitar_store.addViewport(
      this._getViewerId(ViewWindowIndex.Coronal)
    );

    larvitar.larvitar_store.addViewport(
      this._getViewerId(ViewWindowIndex.Axial)
    );

    larvitar.larvitar_store.addViewport(
      this._getViewerId(ViewWindowIndex.Sagittal)
    );

    larvitar.registerNRRDImageLoader();
    larvitar.initializeImageLoader();
    larvitar.registerResliceLoader();
    this._stateManagerProjectService.currentActiveColor = this.activeLabel;

    this._stateManagerProjectService.currentPolylineMode$.subscribe((_) => {
      if (
        this._stateManagerProjectService.currentTool ===
        CornerstoneTool.CustomPolylineFreehand
      ) {
        this.toolActivate(CornerstoneTool.CustomPolylineFreehand, true);
      }
    });
  }

  async processNrrdImage(imageBlob: Blob): Promise<void> {
    this._subscribeToCornerstoneEvents();
    const nrrdFile = new File([imageBlob], IMAGE_FILE_NAME);
    const readFileResult = await Utils.readFileAsync(nrrdFile);
    const volume = larvitar.importNRRDImage(readFileResult);
    this._fillViewSizes(volume);
    const [axialSeriesData, coronalSeriesData, sagittalSeriesData] =
      this._getSeriesDataForAllViews(volume);
    await Promise.all([
      larvitar.renderImage(
        axialSeriesData,
        this._getViewerId(ViewWindowIndex.Axial)
      ),
      larvitar.renderImage(
        coronalSeriesData,
        this._getViewerId(ViewWindowIndex.Coronal)
      ),
      larvitar.renderImage(
        sagittalSeriesData,
        this._getViewerId(ViewWindowIndex.Sagittal)
      ),
    ]);
    larvitar.addDefaultTools();
    this._addCustomTools();
    this.initStackScrollTool();
    
    this._viewerService.uiGridWasChanged
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((_) => {
        this.resize();
      });
  }

  async processNrrdMask(
    maskBlob: Blob,
    isOriginalMask: boolean,
    isUpdatedMaskExist = true
  ): Promise<void> {
    larvitar.initSegmentationModule();
    const nrrdFile = new File([maskBlob], MASK_FILE_NAME);
    const readFileResult = await Utils.readFileAsync(nrrdFile);
    const volume = larvitar.importNRRDImage(readFileResult);
    this._maskHeader = volume.header;
    this._stateManagerProjectService.maskNrrdType =
      NRRD_TYPES_TO_TYPED_ARRAY[this._maskHeader!.type];
    const { configuration } = segModule;
    configuration.arrayType =
      ARRAY_NRRD_TYPES[this._stateManagerProjectService.maskNrrdType];
    Array.from(Array(3)).forEach((_, index) => {
      const i = index + 1;
      const element = this._viewerElements[i];
      setters.activeLabelmapIndex(element, i);
      setters.colorLUTIndexForLabelmap3D(getters.labelmap3D(element, i), i);
    });
    const [axialPixelData, coronalPixelData, sagittalPixelData] =
      this._getPixelData(volume);
    this._maskCustomOrder = volume.customOrder;

    [
      ...new Set([isOriginalMask, isOriginalMask ? isUpdatedMaskExist : false]),
    ].forEach((flag) =>
      this._fillFullPixelData(
        coronalPixelData,
        axialPixelData,
        sagittalPixelData,
        flag
      )
    );

    if (isOriginalMask && isUpdatedMaskExist) {
      return;
    }

    this._nrrdFileOfMask = maskBlob;
    await Promise.all(
      [coronalPixelData, axialPixelData, sagittalPixelData].map((data, i) => {
        const index = i + 1;
        return this._segmentationService.addSegmentationMask(
          this._getInitPropertiesForMask(index),
          data,
          this._getViewerId(index)
        );
      })
    );

    this.setMaskOpacityValue(20);

    Array.from(Array(3)).forEach((_, index) => {
      const i = index + 1;
      const element = this._viewerElements[i];
      setters.activeLabelmapIndex(element, i);
      setters.colorLUTIndexForLabelmap3D(getters.labelmap3D(element, i), i);
    });

    this.toolActivate(CornerstoneTool.EmptyTool);
    this.activeLabel = this._segmentationService.fetchSegmentIndex();
    this._stateManagerProjectService.currentActiveColor = this.activeLabel;
    this._fillSegmentationColors();
    this._setTotalNumberOfSlices();
    this._setMiddleSlices();
    this._stateManagerProjectService.isViewerLoaded = true;
    if(this._stateManagerProjectService.isBoneViewLoaded) {
      this._stateManagerProjectService.isViewChangeClickedReload = false;
    }
  }

  setUndoRedoBeforeViewChange() {
    setTimeout(() => {
      let axialUndo = JSON.parse(sessionStorage.getItem("axialUndo")!);
      let coronalUndo = JSON.parse(sessionStorage.getItem("coronalUndo")!);
      let sagittalUndo = JSON.parse(sessionStorage.getItem("sagittalUndo")!);

      const { coronalLabelmap3D, axialLabelmap3D, sagittalLabelmap3D } = this._getAllElemsAndLabelmaps3D();
      if (coronalUndo) coronalLabelmap3D.undo = coronalUndo;
      if (axialUndo) axialLabelmap3D.undo = axialUndo;
      if (sagittalUndo) sagittalLabelmap3D.undo = sagittalUndo;
      sessionStorage.removeItem("axialUndo");
      sessionStorage.removeItem("coronalUndo");
      sessionStorage.removeItem("sagittalUndo");
      if((this._stateManagerProjectService.isEditAllowed && !this._stateManagerProjectService.isApprovedClicked && !this._stateManagerProjectService.isRejectClicked) && (!this._stateManagerProjectService.isCaseApproved || !this._stateManagerProjectService.isCaseRejected)) {
        this._stateManagerProjectService.isDisabledAllToolsForChangeMask = false;
        this._stateManagerProjectService.isSaveActionDisabled = false;
      } else {
        this._stateManagerProjectService.isDisabledAllToolsForChangeMask = true;
        this._stateManagerProjectService.isSaveActionDisabled = true;
      }

      for (let currentWindow = 1; currentWindow < 4; currentWindow++) {
        const viewerId = this._getViewerId(currentWindow);
        this._stateManagerProjectService.isProcessOfShowDiffHide[viewerId] = false;
      }
    }, 200)

  }

  resetAllViews(isResetViewports = true) {
    if (isResetViewports) {
      setTimeout(() => {
        larvitar.resetViewports([
          this._getViewerId(ViewWindowIndex.Coronal),
          this._getViewerId(ViewWindowIndex.Axial),
          this._getViewerId(ViewWindowIndex.Sagittal),
        ]);
      }, 100);
    }

    this.resetMaskOpacity();
    this.resetViewsEmitter.emit(true);
  }

  setIndexOfSlider(index: number, indexOfWindow: number | null = null): void {
    const displayImage = (i: number) => {
      const element = this._viewerElements[i];
      larvitar.updateStackToolState(this._getViewerId(i), index);
      cornerstone
        .loadImage(
          cornerstoneTools.getToolState(element, 'stack').data[0].imageIds[
          index
          ]
        )
        .then((image) => {
          cornerstone.displayImage(element, image);
        });
    };

    if (indexOfWindow) {
      displayImage(indexOfWindow);
    } else {
      for (let i = 1; i < 4; i++) {
        displayImage(i);
      }
    }
  }

  async getUpdatedMaskBlob(): Promise<Blob> {
    const readFileResult = await Utils.readFileAsync(this._nrrdFileOfMask!);
    const header = this._getHeaderOfNrrdMask(readFileResult as ArrayBuffer);
    const headerBuffer = new ArrayBuffer(header.length);
    const bufView = new Uint8Array(headerBuffer);
    for (let i = 0; i < header.length; i++) {
      bufView[i] = header[i].charCodeAt(0);
    }

    const sourceViewer = this._getViewerId(ViewWindowIndex.Axial);
    let array = this._deepCloneOfTypedArray(
      this._changedFullPixelData.get(sourceViewer)!
    );
    if (this._maskCustomOrder) {
      array = this._transformBackToOriginalPixelData(array, this._maskHeader);
    }
    let buffer: ArrayBufferLike;
    switch (this._maskHeader!.encoding) {
      case 'raw':
        buffer = array.buffer;
        break;
      case 'gzip':
      case 'gz':
        buffer = pako.gzip(array.buffer).buffer;
        break;
      case 'ascii':
      case 'txt':
      case 'text':
        // TODO: now we don't know what to do with this encoding
        throw new Error(
          '"ascii", "txt", "text" encoding will be supported in the future.'
        );
      default:
        throw new Error('Only "raw" and "gzip" encoding are supported.');
    }

    return new Blob([bufView.buffer, buffer]);
  }

  private _deepCloneOfTypedArray(array: TypedArray): TypedArray {
    switch (array.constructor) {
      case Uint8Array: {
        return new Uint8Array(array.buffer);
      }
      case Int8Array: {
        return new Int8Array(array.buffer);
      }
      case Int16Array: {
        return new Int16Array(array.buffer);
      }
      case Uint16Array: {
        return new Uint16Array(array.buffer);
      }
      case Int32Array: {
        return new Int32Array(array.buffer);
      }
      case Uint32Array: {
        return new Uint32Array(array.buffer);
      }
      case Uint8ClampedArray: {
        return new Uint8ClampedArray(array.buffer);
      }
      case Float32Array: {
        return new Float32Array(array.buffer);
      }
      case Float64Array: {
        return new Float64Array(array.buffer);
      }
      default: {
        return new Uint8Array();
      }
    }
  }

  private _transformBackToOriginalPixelData(
    pixelData: TypedArray,
    header: any
  ): TypedArray {
    const spaceDirections = header['space directions'];

    // normalize the space directions
    const normDirections = spaceDirections.map(
      this._pixelDataTransformationService.normalizeVector
    );

    // find the index of the maximum absolute value in each subarray
    const maxArgs =
      this._pixelDataTransformationService.argMaxAbs(normDirections);

    // define new order and signs for axial
    const newOrder = [0, 0, 0];
    let newSigns = [0, 0, 0];

    const signs = normDirections.map((el, i) => (el[maxArgs[i]] < 0 ? -1 : 1));
    newSigns = this._pixelDataTransformationService.reorder(signs, maxArgs);

    // calculate new directions and signs
    for (let i = 0; i < 3; i++) {
      newOrder[maxArgs[i]] = i;
    }

    const newPixelData = this._pixelDataTransformationService.transpose(
      pixelData,
      header.sizes,
      newOrder,
      newSigns
    );

    return newPixelData;
  }

  /**
   * save updated mask
   * @function saveMask
   */
  async saveMask(
    callback?: Function,
    isApproved = false,
    callbackAfterGenerateBoneModel?: Function,
    isDisableButtonAfterComplete = false
  ): Promise<void> {
    const blobOfNewMask = await this.getUpdatedMaskBlob();
    const fileOfUpdatedMask = new File(
      [blobOfNewMask],
      `${this._stateManagerProjectService.caseId}_draft.nrrd`
    );

    this._stateManagerProjectService.isSaveActionDisabled = true;
    if (navigator.onLine) {
      this._dataRequestService
        .saveNrrdMask(
          this._stateManagerProjectService.caseId,
          fileOfUpdatedMask,
          isApproved,
          false
        )
        .subscribe({
          next: () => {
            this._projectActionsRequestService
              .requestUpdateEditCaseStatus(
                this._stateManagerProjectService.caseId,
                1,
                this._stateManagerProjectService.currentUserName
              ).subscribe();
            this._sendEditAndResetLogs();
            this._dataRequestService
              .generateBoneModel(
                this._stateManagerProjectService.caseId,
                isApproved,
                false
              )
              .subscribe(() => {
                if (this._stateManagerProjectService.isMaskWasChanged) {
                  this._stateManagerProjectService.boneModelType =
                    BoneModelType.Draft;
                }

                callbackAfterGenerateBoneModel?.();
              });

            this._stateManagerProjectService.isSaveActionDisabled =
              isDisableButtonAfterComplete;

            callback?.();
          },
          error: () => {
            this._confirmDialog
              .openConfirmDialog(
                TextsForErrorsDialogs.PROCESSING_ERROR_PLEASE_REFRESH,
                TextsForErrorsDialogs.CLOSE,
                ''
              )
              .subscribe((_) => { });
            this._stateManagerProjectService.isSaveActionDisabled = false;
          },
        });
    } else {
      FileSaver.saveAs(blobOfNewMask, MASK_FILE_NAME);
    }
  }

  async saveMaskV2(
    callback?: Function,
    isApproved = false,
    isDisableButtonAfterComplete = false
  ): Promise<void> {
    const blobOfNewMask = await this.getUpdatedMaskBlob();
    const fileOfUpdatedMask = new File(
      [blobOfNewMask],
      `${this._stateManagerProjectService.caseId}_draft.nrrd`
    );

    this._stateManagerProjectService.isSaveActionDisabled = true;
    if (navigator.onLine) {
      this._dataRequestService
        .saveNrrdMask(
          this._stateManagerProjectService.caseId,
          fileOfUpdatedMask,
          isApproved,
          false
        )
        .subscribe({
          next: () => {
            this._projectActionsRequestService
              .requestUpdateEditCaseStatus(
                this._stateManagerProjectService.caseId,
                1,
                this._stateManagerProjectService.currentUserName
              ).subscribe();
            this._sendEditAndResetLogs();
            this._dataRequestService
              .generateBoneModel(
                this._stateManagerProjectService.caseId,
                isApproved,
                false
              )
              .subscribe(() => {
                if (this._stateManagerProjectService.isMaskWasChanged) {
                  this._stateManagerProjectService.boneModelType =
                    BoneModelType.Draft;
                }
              });
            this._stateManagerProjectService.isSaveActionDisabled =
              isDisableButtonAfterComplete;

            callback?.();
          },
          error: () => {
            this._confirmDialog
              .openConfirmDialog(
                TextsForErrorsDialogs.PROCESSING_ERROR_PLEASE_REFRESH,
                TextsForErrorsDialogs.CLOSE,
                ''
              )
              .subscribe((_) => { });
            this._stateManagerProjectService.isSaveActionDisabled = false;
          },
        });
    } else {
      FileSaver.saveAs(blobOfNewMask, MASK_FILE_NAME);
    }
  }

  async saveMasks(callback) {
    const isApproved = false;
    const isDisableButtonAfterComplete = false;
    const blobOfNewMask = await this.getUpdatedMaskBlob();
    const fileOfUpdatedMask = new File(
      [blobOfNewMask],
      `${this._stateManagerProjectService.caseId}_draft.nrrd`
    );
    this._dataRequestService
      .saveNrrdMasks(
        this._stateManagerProjectService.caseId,
        fileOfUpdatedMask,
        isApproved,
        false
      )
      .subscribe({
        next: () => {
          this._projectActionsRequestService
            .requestUpdateEditCaseStatus(
              this._stateManagerProjectService.caseId,
              1,
              this._stateManagerProjectService.currentUserName
            ).subscribe();
          this._sendEditAndResetLogs();
          this._dataRequestService
            .generateBoneModels(
              this._stateManagerProjectService.caseId,
              isApproved,
              false
            )
            .subscribe(() => {
              if (this._stateManagerProjectService.isMaskWasChanged) {
                this._stateManagerProjectService.boneModelType =
                  BoneModelType.Draft;
              }
              callback?.();
            });

          this._stateManagerProjectService.isSaveActionDisabled =
            isDisableButtonAfterComplete;
          // callback?.();
        },
        error: () => {
          this._confirmDialog
            .openConfirmDialog(
              TextsForErrorsDialogs.PROCESSING_ERROR_PLEASE_REFRESH,
              TextsForErrorsDialogs.CLOSE,
              ''
            )
            .subscribe((_) => { });
          this._stateManagerProjectService.isSaveActionDisabled = false;
        },
      });
  }

  /**
   * activate choosen tool
   * @function toolActivate
   * @param {CornerstoneTool} tool - cornerstone tool
   * @param {boolean} isChangeMode - method was called for change tool mode
   */
  toolActivate(tool: CornerstoneTool, isChangeMode: boolean = false): void {
    if (
      !Object.values(CornerstoneTool).includes(tool) ||
      (this._stateManagerProjectService.currentTool === tool &&
        this._stateManagerProjectService.currentTool === CornerstoneTool.Wwwc)
    ) {
      return;
    }
    let toolForActivation = tool;

    if (
      this._stateManagerProjectService.currentTool === tool &&
      !isChangeMode
    ) {
      toolForActivation = CornerstoneTool.EmptyTool;
      this.activeMouseWheelTool();
    }

    if (
      this._stateManagerProjectService.currentTool ===
      CornerstoneTool.CustomPolylineFreehand &&
      toolForActivation !== CornerstoneTool.CustomPolylineFreehand
    ) {
      cornerstoneTools.setToolDisabled(CornerstoneTool.CustomPolylineFreehand);
    }

    this._stateManagerProjectService.currentTool = toolForActivation;

    if (
      ![CornerstoneTool.Pan, CornerstoneTool.Zoom].includes(
        toolForActivation
      ) &&
      this._stateManagerProjectService.isActiveShowChangesMode
    ) {
      this._confirmDialog
        .openConfirmDialog(
          'Dialog.VIEW_CHANGES_CANCEL_MESSAGE',
          'Dialog.ConfirmButtons.OK'
        )
        .subscribe((res) => {
          this.cancelShowDiff();
        });
    }

    const viewerId = this._getViewerId(
      this._stateManagerProjectService.currentSelectedWindow
    );

    if (
      [
        CornerstoneTool.CircleBrush,
        CornerstoneTool.SquareBrush,
        CornerstoneTool.EraseCircleBrush,
        CornerstoneTool.EraseSquareBrush,
        CornerstoneTool.CustomPolylineFreehand,
        CornerstoneTool.ShowDiff,
      ].includes(toolForActivation) &&
      (this._stateManagerProjectService.hidenColors![viewerId] && this._stateManagerProjectService.hidenColors![viewerId].length !== 0)
    ) {
      this._updateChangeColorStates();
    }

    cornerstoneTools.setToolActive(toolForActivation, {
      mouseButtonMask: CornerstoneMouseButton.Left,
      ...(toolForActivation === CornerstoneTool.CustomPolylineFreehand && {
        isDefaultBehavior:
          this._stateManagerProjectService.currentPolylineMode === 'DEFAULT',
      }),
    });
  }

  initStackScrollTool(): void {
    cornerstoneTools.removeTool(
      CornerstoneAdditionalTool.StackScrollMouseWheel
    );
    cornerstoneTools.addTool(StackScrollMouseWheelTool, {
      loop: false,
      allowSkipping: true,
      invert: false,
    });
    cornerstoneTools.StackScrollMouseWheelTool = StackScrollMouseWheelTool;
  }

  activeMouseWheelTool(): void {
    let invert = false;
    switch (this._stateManagerProjectService.currentSelectedWindow) {
      case ViewWindowIndex.Sagittal:
      case ViewWindowIndex.Axial: {
        invert = true;
        break;
      }
      default: {
        break;
      }
    }
    this.additionalToolActivate(
      CornerstoneAdditionalTool.StackScrollMouseWheel,
      CornerstoneMouseButton.None,
      {
        invert: invert,
      }
    );
  }

  /**
   * Activates choosen tool without affecting the main tools
   * @param tool tool for activation
   * @param button mouse button for the tool
   */
  additionalToolActivate(
    tool: CornerstoneAdditionalTool,
    button = CornerstoneMouseButton.None,
    options: OptionsForTool = {}
  ) {
    cornerstoneTools.setToolActive(tool, {
      mouseButtonMask: button,
      ...options,
    });
  }

  toolDeactivate(
    tool: CornerstoneTool | CornerstoneAdditionalTool,
    button = CornerstoneMouseButton.None
  ) {
    if (!button) {
      cornerstoneTools.setToolDisabled(tool);
      return;
    }

    cornerstoneTools.setToolActive(CornerstoneTool.EmptyTool, {
      mouseButtonMask: button,
    });
  }

  getMaskOpacityValue(): number {
    return this._maskOpacity$.getValue();
  }

  isMaskOpacityValue(): Observable<number> {
    return this._maskOpacity$.asObservable();
  }

  setMaskOpacityValue(value: number): void {
    this._maskOpacity$.next(value);
  }

  /**
   * set new value of mask opacity
   * @function setMaskOpacity
   * @param {number} value - value of opacity
   */
  setMaskOpacity(value: number, forSave: boolean = true): void {
    if (forSave) {
      this.setMaskOpacityValue(value);
    }
    for (let index = 1; index < 4; index++) {
      this._maskOpacitySetters(value, index);
    }
  }

  resetMaskOpacity(): void {
    this._maskOpacity$.next(20);

    for (let index = 1; index < 4; index++) {
      this._maskOpacitySetters(20, index);
    }
  }

  getBrushSize(): number {
    return cornerstoneTools.getModule('segmentation').configuration.radius;
  }

  /**
   * set new size of brush
   * @function setBrushSize
   * @param {number} size - new size of brush
   */
  setBrushSize(size: number): void {
    cornerstoneTools.getModule('segmentation').setters.radius(size);
  }

  /**
   * change the selected color layer
   * @function switchLabelMap
   * @param {number} index - index of color
   */
  switchLabelMap(index: number): void {
    this.activeLabel = index;
    for (let i = 1; i < 4; i++) {
      const element = this._viewerElements[i];
      setters.activeSegmentIndex(element, index);
    }
    this._stateManagerProjectService.currentActiveColor = this.activeLabel;
  }

  /**
   * activate tool for show difference between original and updated masks
   * @function activateShowDiff
   */
  activateShowDiff(): void {
    this.toolActivate(CornerstoneTool.ShowDiff);

    for (let i = 1; i < 4; i++) {
      const viewerId = this._getViewerId(i);
      const element = this._viewerElements[i];
      const { undo, redo } = getters.labelmap2D(element).labelmap3D;
      if (!this._stateManagerProjectService.isActiveShowChangesMode) {
        this._undoBeforeShowDiff[viewerId] = undo;
        this._redoBeforeShowDiff[viewerId] = redo;
      }
    }

    if (!this._stateManagerProjectService.isActiveShowChangesMode) {
      this.setMaskOpacity(50, false);
      for (let i = 1; i < 4; i++) {
        this.activateShowDiffForSlice(i);
      }
    }

    this._stateManagerProjectService.is3dUpdateNeeded = false;
    this._stateManagerProjectService.isSaveActionDisabled = true;
    if (!this._stateManagerProjectService.isActiveShowChangesMode) {
      this._stateManagerProjectService.isActiveShowChangesMode =
        !this._stateManagerProjectService.isActiveShowChangesMode;
    }
  }

  /**
   * deactivate tool for show difference between original and updated masks
   * @function cancelShowDiff
   */
  async cancelShowDiff(): Promise<void> {
    for (let currentWindow = 1; currentWindow < 4; currentWindow++) {
      const viewerId = this._getViewerId(currentWindow);
      if (this._stateManagerProjectService.isActiveShowChangesMode) {
        this.setMaskOpacity(this.getMaskOpacityValue(), false);
        this._stateManagerProjectService.isProcessOfShowDiffHide[viewerId] =
          true;

        this._cancelMaskForShowDiff(currentWindow, viewerId);

        this._mapOfRenderedSlicesForShowChanges.set(currentWindow, []);
      }
    }
    if((this._stateManagerProjectService.isEditAllowed && !this._stateManagerProjectService.isApprovedClicked && !this._stateManagerProjectService.isRejectClicked) && (!this._stateManagerProjectService.isCaseApproved || !this._stateManagerProjectService.isCaseRejected)) {
      this.onViewChangesReload();
    }
  }

  /**
   * reload the windows views and save the changes
   */
  async onViewChangesReload() {
    for (let currentWindow = 1; currentWindow < 4; currentWindow++) {
      const viewerId = this._getViewerId(currentWindow);
      this._stateManagerProjectService.isProcessOfShowDiffHide[viewerId] = true;

    }
    const blobOfNewMask = await this.getUpdatedMaskBlob();
    const fileOfUpdatedMask = new File(
      [blobOfNewMask],
      `${this._stateManagerProjectService.caseId}_draft.nrrd`
    );

    this._stateManagerProjectService.isSaveActionDisabled = true;
    if (navigator.onLine) {
      sessionStorage.setItem("axialUndo", JSON.stringify((getters.labelmap3D(this._viewerElements[ViewWindowIndex.Axial], ViewWindowIndex.Axial).undo)));
      sessionStorage.setItem("coronalUndo", JSON.stringify((getters.labelmap3D(this._viewerElements[ViewWindowIndex.Coronal], ViewWindowIndex.Coronal).undo)));
      sessionStorage.setItem("sagittalUndo", JSON.stringify((getters.labelmap3D(this._viewerElements[ViewWindowIndex.Sagittal], ViewWindowIndex.Sagittal).undo)));
        this._dataRequestService
        .saveNrrdMask(
          this._stateManagerProjectService.caseId,
          fileOfUpdatedMask,
          false,
          false
        )
        .subscribe({
          next: () => {
            this._stateManagerProjectService.isChangesBetweenMasks = true;
            this._stateManagerProjectService.isViewChangeClickedReload = true;
            this._stateManagerProjectService.isBoneViewLoaded = false;
            this._dataRequestService
              .generateBoneModel(
                this._stateManagerProjectService.caseId,
                false,
                false
              )
              .subscribe(() => {
                if (this._stateManagerProjectService.isMaskWasChanged) {
                  this._stateManagerProjectService.boneModelType =
                    BoneModelType.Draft;
                }

              });

            //window.location.reload();
          },
          error: () => {
            this._confirmDialog
              .openConfirmDialog(
                TextsForErrorsDialogs.PROCESSING_ERROR_PLEASE_REFRESH,
                TextsForErrorsDialogs.CLOSE,
                ''
              )
              .subscribe((_) => { });
            this._stateManagerProjectService.isSaveActionDisabled = false;
          },
        });
    } else {
      FileSaver.saveAs(blobOfNewMask, MASK_FILE_NAME);
    }
  }

  /**
   * activate tool for show difference between original and updated masks in slices
   * @function activateShowDiffForSlice
   */
  activateShowDiffForSlice(index: ViewWindowIndex): void {
    const viewerId = this._getViewerId(index);
    const element = this._viewerElements[index];
    const newPixelData = this._returnNewPixelData(
      true,
      this._basedFullPixelData.get(viewerId),
      null,
      index
    );

    const elementsForSlice =
      newPixelData.length /
      (this._stateManagerProjectService.totalNumberOfSlices[viewerId] + 1);
    const startIndexOfSlice =
      elementsForSlice *
      this._stateManagerProjectService.currentActiveSliceIndex[viewerId];
    const endIndexOfSlice = startIndexOfSlice + elementsForSlice;
    const pixelDataForSlice: number[] = [];

    for (let i = startIndexOfSlice; i < endIndexOfSlice; i++) {
      const elementOfBasedPixelData =
        this._basedFullPixelData.get(viewerId)![i];
      const elementOfpixelDataBeforeAcrivatedViewDiff =
        this._changedFullPixelData.get(viewerId)![i];
      if (
        (elementOfBasedPixelData !== 0 &&
          elementOfpixelDataBeforeAcrivatedViewDiff === 0) ||
        (elementOfBasedPixelData === 0 &&
          elementOfpixelDataBeforeAcrivatedViewDiff !== 0) ||
        (elementOfBasedPixelData !== 0 &&
          elementOfpixelDataBeforeAcrivatedViewDiff !== 0 &&
          elementOfBasedPixelData !== elementOfpixelDataBeforeAcrivatedViewDiff)
      ) {
        newPixelData[i] = 5;
      }
      pixelDataForSlice.push(newPixelData[i]);
    }
    const newLabelmap2D = getters.labelmap2D(element);
    newLabelmap2D.labelmap2D.pixelData = pixelDataForSlice;
    setters.updateSegmentsOnLabelmap2D(newLabelmap2D);

    this._mapOfRenderedSlicesForShowChanges
      .get(index)!
      .push(this._stateManagerProjectService.currentActiveSliceIndex[viewerId]);
  }

  private _cancelMaskForShowDiff(
    currentWindow: number,
    viewerId: string
  ): void {
    const slicesForChange =
      this._mapOfRenderedSlicesForShowChanges.get(currentWindow);
    const element = this._viewerElements[currentWindow];
    const labelmap3D = getters.labelmap3D(element);

    if (!slicesForChange) {
      return;
    }

    slicesForChange.forEach((sliceIndex) => {
      const pixelDataForSlice = this._returnNewPixelData(
        false,
        this._changedFullPixelData.get(viewerId),
        sliceIndex,
        currentWindow
      );

      labelmap3D.labelmaps2D[sliceIndex].pixelData = pixelDataForSlice;
    });

    const processObject =
      this._stateManagerProjectService.isProcessOfShowDiffHide;
    processObject[viewerId] = false;
    this._stateManagerProjectService.isProcessOfShowDiffHide = processObject;

    setTimeout(() => {
      const labelmap3D = getters.labelmap2D(element).labelmap3D;
      labelmap3D.undo = this._undoBeforeShowDiff[viewerId];
      labelmap3D.redo = this._redoBeforeShowDiff[viewerId];
      this._undoBeforeShowDiff[viewerId] = [];
      this._redoBeforeShowDiff[viewerId] = [];

      let isShowDiffMustBeOff = true;
      for (const key in this._undoBeforeShowDiff) {
        if (this._undoBeforeShowDiff[key].length > 0) {
          isShowDiffMustBeOff = false;
        }
      }
      if (isShowDiffMustBeOff) {
        this._stateManagerProjectService.isActiveShowChangesMode =
          !this._stateManagerProjectService.isActiveShowChangesMode;
      }
    }, 0);
  }

  /**
   * Show only current color's layer
   * @function showOnlyCurrentColor
   * @param {number} colorIndex - index of current color
   */
  showOnlyCurrentColor(colorIndex: number): void {
    const viewerId = this._getViewerId(
      this._stateManagerProjectService.currentSelectedWindow
    );

    const newPixelData = this._returnNewPixelData(
      true,
      this._changedFullPixelData.get(viewerId),
      null,
      this._stateManagerProjectService.currentSelectedWindow
    );
    for (let i = 0; i < newPixelData.length; i++) {
      if (newPixelData[i] !== colorIndex) {
        newPixelData[i] = 0;
      }
    }

    const properties = {
      opacity: this.getMaskOpacityValue() / 100,
      labelId: this._stateManagerProjectService.currentSelectedWindow,
    };
    this.addSegmentationMask(
      properties,
      newPixelData,
      this._stateManagerProjectService.currentSelectedWindow
    );
  }

  /**
   * Hide or show color's layer
   * @function changeVisibilityOfColor
   * @param {number} colorIndex - index of current color
   * @param {boolean} isVisible - show or hide color
   */
  changeVisibilityOfColor(colorIndex: number, isVisible: boolean): void {
    for (let i = 1; i < 4; i++) {
      const currentWindow = i;
      const viewerId = this._getViewerId(currentWindow);

      const element = this._viewerElements[currentWindow];
      const labelmap3D = getters.labelmap2D(element).labelmap3D;
      const segmentsHidden = labelmap3D.segmentsHidden;
      segmentsHidden[colorIndex] = !isVisible;
      external.cornerstone.updateImage(this._viewerElements[currentWindow]);

      if (isVisible) {
        const findedIndex = this._stateManagerProjectService.hidenColors![
          viewerId
        ].findIndex((el) => el === colorIndex);
        this._stateManagerProjectService.hidenColors![viewerId].splice(
          findedIndex,
          1
        );
      } else {
        this._stateManagerProjectService.hidenColors![viewerId].push(
          colorIndex
        );
      }
    }
  }

  private _updateChangeColorStates(): void {
    for (let i = 1; i < 4; i++) {
      const currentWindow = i;
      const viewerId = this._getViewerId(currentWindow);
      this._stateManagerProjectService.hidenColors![viewerId] = [];
      this._stateManagerProjectService.colorsData![viewerId].forEach(
        (el: IColorData) => {
          el.state = true;
        }
      );

      const element = this._viewerElements[currentWindow];
      const labelmap3D = getters.labelmap2D(element).labelmap3D;
      const segmentsHidden = labelmap3D.segmentsHidden;
      for (let i = 0; i < segmentsHidden.length; i++) {
        segmentsHidden[i] = false;
      }
      labelmap3D.segmentsHidden = segmentsHidden;
      cornerstone.updateImage(this._viewerElements[currentWindow]);
    }
  }

  /**
   * Set cornerstone tools custom configuration (extend default configuration)
   * @function setToolsStyle
   * @param {Object} style - the style object (see tools.defaults.js)
   */
  setToolsStyle(): void {
    const fontFamily = DEFAULT_STYLE.fontFamily;
    const fontSize = DEFAULT_STYLE.fontSize;

    cornerstoneTools.toolStyle.setToolWidth(DEFAULT_STYLE.width);
    cornerstoneTools.toolColors.setToolColor(DEFAULT_STYLE.color);
    cornerstoneTools.toolColors.setActiveColor(DEFAULT_STYLE.activeColor);
    cornerstoneTools.toolColors.setFillColor(DEFAULT_STYLE.fillColor);
    cornerstoneTools.textStyle.setFont(`${fontSize}px ${fontFamily}`);
    cornerstoneTools.textStyle.setBackgroundColor(
      DEFAULT_STYLE.backgroundColor
    );
  }

  getMapOfViewedSlicesForCurrentWindow(): boolean[] {
    return this._mapOfViewedSlices.get(
      this._stateManagerProjectService.currentSelectedWindow
    )!;
  }

  getMapOfViewedSlices(window: number): boolean[] {
    return this._mapOfViewedSlices.get(window)!;
  }

  /**
   * change segmentation
   * @function addSegmentationMask
   * @param {any} options - options of new segmentation
   * @param {any} pixelData
   */
  addSegmentationMask(
    options: any,
    pixelData: TypedArray,
    indexOfWindow: number,
    isBlockForUpdate: boolean = false
  ): void {
    const viewerId = this._getViewerId(indexOfWindow);
    this._segmentationService
      .addSegmentationMask(options, pixelData, viewerId)
      .then(() => {
        setTimeout(() => {
          const element = this._viewerElements[indexOfWindow];
          setters.activeLabelmapIndex(element, indexOfWindow);
          setters.activeSegmentIndex(element, this.activeLabel);
          this._stateManagerProjectService.currentActiveColor =
            this.activeLabel;
          external.cornerstone.updateImage(element);
        }, 200);
      });
  }

  undo(): void {
    const {
      coronalElem,
      axialElem,
      sagittalElem,
      coronalLabelmap3D,
      axialLabelmap3D,
      sagittalLabelmap3D,
    } = this._getAllElemsAndLabelmaps3D();

    if (
      new Set([
        coronalLabelmap3D.undo.length,
        axialLabelmap3D.undo.length,
        sagittalLabelmap3D.undo.length,
      ]).size !== 1
    ) {
      console.error(
        'The number of elements in the undo arrays for all views is different'
      );
      return;
    }


    const { undo: axialUndo } = axialLabelmap3D;
    const { undo: coronalUndo } = coronalLabelmap3D;
    const { undo: sagittalUndo } = sagittalLabelmap3D;

    if (this._stateManagerProjectService.currentSelectedWindow == 1) { // coronal 
      if (coronalUndo[coronalUndo.length - 1].length == 1) {
        this.setIndexOfSlider(coronalUndo[coronalUndo.length - 1][0]['imageIdIndex'], this._stateManagerProjectService.currentSelectedWindow)
        setTimeout(() => {
          setters.undo(
            coronalElem,
            `viewer${ViewWindowIndex.Coronal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.undo(
            axialElem,
            `viewer${ViewWindowIndex.Axial}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.undo(
            sagittalElem,
            `viewer${ViewWindowIndex.Sagittal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
        }, 100);
      } else {
        if (axialUndo[axialUndo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_AXIAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else if (sagittalUndo[sagittalUndo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_SAGITTAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_VIEW_CHANGE_MESSAGE',
            'Dialog.ConfirmButtons.OK'
          );
        }
      }
    } else if (this._stateManagerProjectService.currentSelectedWindow == 2) { // axial
      if (axialUndo[axialUndo.length - 1].length == 1) {
        this.setIndexOfSlider(axialUndo[axialUndo.length - 1][0]['imageIdIndex'], this._stateManagerProjectService.currentSelectedWindow)
        setTimeout(() => {
          setters.undo(
            coronalElem,
            `viewer${ViewWindowIndex.Coronal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.undo(
            axialElem,
            `viewer${ViewWindowIndex.Axial}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.undo(
            sagittalElem,
            `viewer${ViewWindowIndex.Sagittal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
        }, 100);
      } else {
        if (coronalUndo[coronalUndo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_CORONAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else if (sagittalUndo[sagittalUndo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_SAGITTAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_VIEW_CHANGE_MESSAGE',
            'Dialog.ConfirmButtons.OK'
          );
        }
      }
    } else { // sagittal
      if (sagittalUndo[sagittalUndo.length - 1].length == 1) {
        this.setIndexOfSlider(sagittalUndo[sagittalUndo.length - 1][0]['imageIdIndex'], this._stateManagerProjectService.currentSelectedWindow)
        setTimeout(() => {
          setters.undo(
            coronalElem,
            `viewer${ViewWindowIndex.Coronal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.undo(
            axialElem,
            `viewer${ViewWindowIndex.Axial}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.undo(
            sagittalElem,
            `viewer${ViewWindowIndex.Sagittal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
        }, 100);
      } else {
        if (coronalUndo[coronalUndo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_CORONAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else if (axialUndo[axialUndo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_AXIAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_VIEW_CHANGE_MESSAGE',
            'Dialog.ConfirmButtons.OK'
          );
        }
      }
    }
  }

  redo(): void {
    const {
      coronalElem,
      axialElem,
      sagittalElem,
      coronalLabelmap3D,
      axialLabelmap3D,
      sagittalLabelmap3D,
    } = this._getAllElemsAndLabelmaps3D();

    if (
      new Set([
        coronalLabelmap3D.redo.length,
        axialLabelmap3D.redo.length,
        sagittalLabelmap3D.redo.length,
      ]).size !== 1
    ) {
      console.error(
        'The number of elements in the redo arrays for all views is different'
      );
      return;
    }

    const { redo: axialRedo } = axialLabelmap3D;
    const { redo: coronalRedo } = coronalLabelmap3D;
    const { redo: sagittalRedo } = sagittalLabelmap3D;
    if (this._stateManagerProjectService.currentSelectedWindow == 1) { // coronal 

      if (coronalRedo[coronalRedo.length - 1].length == 1) {
        this.setIndexOfSlider(coronalRedo[coronalRedo.length - 1][0]['imageIdIndex'], this._stateManagerProjectService.currentSelectedWindow)

        setTimeout(() => {
          setters.redo(
            coronalElem,
            `viewer${ViewWindowIndex.Coronal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.redo(
            axialElem,
            `viewer${ViewWindowIndex.Axial}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.redo(
            sagittalElem,
            `viewer${ViewWindowIndex.Sagittal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
        }, 100);
      } else {
        // alert('Please change the dropdown to apply further undo operations.')
        if (axialRedo[axialRedo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_AXIAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else if (sagittalRedo[sagittalRedo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_SAGITTAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_VIEW_CHANGE_MESSAGE',
            'Dialog.ConfirmButtons.OK'
          );
        }
      }
    } else if (this._stateManagerProjectService.currentSelectedWindow == 2) { // axial
      if (axialRedo[axialRedo.length - 1].length == 1) {
        this.setIndexOfSlider(axialRedo[axialRedo.length - 1][0]['imageIdIndex'], this._stateManagerProjectService.currentSelectedWindow)

        setTimeout(() => {
          setters.redo(
            coronalElem,
            `viewer${ViewWindowIndex.Coronal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.redo(
            axialElem,
            `viewer${ViewWindowIndex.Axial}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.redo(
            sagittalElem,
            `viewer${ViewWindowIndex.Sagittal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
        }, 100);
      } else {
        // alert('Please change the dropdown to apply further undo operations.')
        if (coronalRedo[coronalRedo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_CORONAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else if (sagittalRedo[sagittalRedo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_SAGITTAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_VIEW_CHANGE_MESSAGE',
            'Dialog.ConfirmButtons.OK'
          );
        }
      }
    } else { // sagittal
      if (sagittalRedo[sagittalRedo.length - 1].length == 1) {

        this.setIndexOfSlider(sagittalRedo[sagittalRedo.length - 1][0]['imageIdIndex'], this._stateManagerProjectService.currentSelectedWindow)

        setTimeout(() => {
          setters.redo(
            coronalElem,
            `viewer${ViewWindowIndex.Coronal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.redo(
            axialElem,
            `viewer${ViewWindowIndex.Axial}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
          setters.redo(
            sagittalElem,
            `viewer${ViewWindowIndex.Sagittal}`,
            this._stateManagerProjectService.hidenColors,
            this._undoRedoService.undoRedoEmitter
          );
        }, 100);
      } else {
        if (coronalRedo[coronalRedo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_CORONAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else if (axialRedo[axialRedo.length - 1].length == 1) {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_IN_AXIAL_VIEW',
            'Dialog.ConfirmButtons.OK'
          );
        } else {
          this.alertDialogService.openConfirmDialog(
            'Dialog.UNDO_REDO_VIEW_CHANGE_MESSAGE',
            'Dialog.ConfirmButtons.OK'
          );
        }
      }
    }
  }

  isUndoEmpty(): boolean {
    const { coronalLabelmap3D, axialLabelmap3D, sagittalLabelmap3D } =
      this._getAllElemsAndLabelmaps3D();

    return (
      !coronalLabelmap3D.undo.length &&
      !axialLabelmap3D.undo.length &&
      !sagittalLabelmap3D.undo.length
    );
  }

  isRedoEmpty(): boolean {
    const { coronalLabelmap3D, axialLabelmap3D, sagittalLabelmap3D } =
      this._getAllElemsAndLabelmaps3D();

    return (
      !coronalLabelmap3D.redo.length &&
      !axialLabelmap3D.redo.length &&
      !sagittalLabelmap3D.redo.length
    );
  }

  resize(): void {
    Array.from(Array(3)).forEach((_, index) => {
      const i = index + 1;
      const element = this._viewerElements[i];
      if (element && this._stateManagerProjectService.isViewerLoaded) {
        cornerstone.resize(element, true);
      }
    });
  }

  /**
   * restore tool selection
   * @function restore
   */

  restore(): void {
    const element =
      this._viewerElements[
      this._stateManagerProjectService.currentSelectedWindow
      ];
    external.cornerstone.reset(element);
  }

  /**
   * clear service
   * @function destroy
   */
  destroy(): void {
    this.resetMaskOpacity();
    this._referenceLineService.destroy();
    segModule.state.series = [];
    larvitar.clearImageCache();

    larvitar.resetLarvitarManager();
    larvitar.clearMultiFrameCache();
    larvitar.clearMeasurements();
    larvitar.clearCornerstoneElements();

    // clear all tools
    larvitar.clearToolStateByName();

    this._nrrdFileOfMask = undefined;
    this.activeLabel = this._segmentationService.fetchSegmentIndex();
    Array.from(Array(3)).forEach((_, index) => {
      const i = index + 1;
      this._mapOfRenderedSlicesForShowChanges.set(i, []);
    });

    this.setMaskOpacityValue(50);

    this._basedFullPixelData = new Map<string, TypedArray>();
    this._changedFullPixelData = new Map<string, TypedArray>();
    this._maskHeader = undefined;
    this.changedSlicesForUpdateInfo = [];
    this._stateManagerProjectService.isViewerLoaded = false;
    this._mapOfViewedSlices = new Map<number, boolean[]>();
    this._logsForEditEvents = new Map<string, number[]>();
    this._isResetCalled = false;
    for (let i = 1; i < 4; i++) {
      const element = this._viewerElements[i];
      larvitar.disableViewport(this._getViewerId(i));
      this._mapOfViewedSlices.set(i, []);
      this._logsForEditEvents.set(this._getViewName(i), []);
      cornerstone.disable(element);
    }

    this._viewerElements = {};
    this._isStartLoadOfMaskForViewFinish = {
      viewer1: false,
      viewer2: false,
      viewer3: false,
    };

    this._unsubscribe$.next(undefined);
    this._unsubscribe$.complete();
    this._breakViewOfSlice$.next(undefined);
    this._breakViewOfSlice$.complete();
  }

  /**
   * activate tool for reset difference between original and updated masks
   * @function activateResetDiff
   */
  activateResetDiff(): void {
    Array.from(Array(3)).forEach((_, index) => {
      const i = index + 1;
      const viewerId = this._getViewerId(i);

      this._stateManagerProjectService.isProcessOfResetDiff[viewerId] = true;

      this._changedFullPixelData.set(
        viewerId,
        this._returnNewPixelData(
          true,
          this._basedFullPixelData.get(viewerId),
          null,
          i
        )
      );

      const properties = {
        opacity: this.getMaskOpacityValue() / 100,
        labelId: i,
      };

      this._segmentationService
        .addSegmentationMask(
          properties,
          this._changedFullPixelData.get(viewerId),
          viewerId
        )
        .then(() => {
          external.cornerstone.updateImage(this._viewerElements[i]);
          const processOfResetDiff =
            this._stateManagerProjectService.isProcessOfResetDiff;
          processOfResetDiff[viewerId] = false;
          this._stateManagerProjectService.isProcessOfResetDiff =
            processOfResetDiff;

          if (
            processOfResetDiff[this._getViewerId(ViewWindowIndex.Coronal)] ===
            false &&
            processOfResetDiff[this._getViewerId(ViewWindowIndex.Axial)] ===
            false &&
            processOfResetDiff[this._getViewerId(ViewWindowIndex.Sagittal)] ===
            false
          ) {
            this.resetChangesEmitter.emit(true);
            this._isResetCalled = true;

            setTimeout(() => {
              this._stateManagerProjectService.isMaskWasChanged = false;
              this._logsForEditEvents = new Map<string, number[]>();
              for (let i = 1; i < 4; i++) {
                this._logsForEditEvents.set(this._getViewName(i), []);
              }
            }, 0);
          }
        });
    });
  }

  /**
   * get header of original mask for create updated mask
   * @function _getHeaderOfNrrdMask
   * @param {ArrayBuffer} nrrdBuffer
   */
  private _getHeaderOfNrrdMask(nrrdBuffer: ArrayBuffer): string[] {
    const byteArrayHeader: string[] = [];
    const view = new DataView(nrrdBuffer);

    for (let i = 0, length = nrrdBuffer.byteLength; i < length; i++) {
      byteArrayHeader.push(String.fromCharCode(view.getUint8(i)));

      if (
        i > 0 &&
        byteArrayHeader[i - 1] === '\n' &&
        byteArrayHeader[i] === '\n'
      ) {
        break;
      }
    }

    return byteArrayHeader;
  }

  /**
   * a function called for return needed pixeldata
   * @function _returnNewPixelData
   * @param {boolean} isFullPixelData
   * @param {TypedArray | undefined} currentPixelData
   * @param {number | null} currentSlice
   */
  private _returnNewPixelData(
    isFullPixelData: boolean,
    currentPixelData: TypedArray | undefined = undefined,
    currentSlice: number | null = null,
    indexOfWindow: number
  ): TypedArray {
    const element = this._viewerElements[indexOfWindow];
    const index =
      currentSlice !== null
        ? currentSlice
        : cornerstoneTools
          .getToolState(element, 'stack')
          .data[0].imageIds?.findIndex(
            (el) => el === this._stateManagerProjectService.currentActiveSlice
          );

    const { labelmap2D } = getters.labelmap2D(element);
    const buffer = currentPixelData?.buffer || labelmap2D.pixelData.buffer;
    const numberOfFrames = cornerstoneTools.getToolState(element, 'stack')
      .data[0].imageIds.length;
    const slicelengthInBytes = buffer.byteLength / numberOfFrames;
    const arrayType = NRRD_TYPES_TO_TYPEDARRAY[this._maskHeader!.type];
    if (isFullPixelData) {
      return new arrayType(buffer.slice());
    } else {
      try {
        const startByte = slicelengthInBytes * index;
        const endByte = startByte + slicelengthInBytes;

        // Ensure we don't exceed buffer bounds
        if (startByte >= buffer.byteLength || endByte > buffer.byteLength) {
          console.error('Slice bounds exceed buffer size:', {
            startByte,
            endByte,
            bufferSize: buffer.byteLength,
            slicelengthInBytes,
            index,
            numberOfFrames
          });
          throw new Error('Slice bounds exceed buffer size');
        }

        // Create slice with explicit bounds
        const sliceBuffer = buffer.slice(startByte, endByte);
        return new arrayType(sliceBuffer);
      }
      catch (error) {
        console.log(error);
        throw error;
      }
    }
  }

  /**
   * a function called by events to track change number of current slice
   * @function _onNewImage
   * @param {number} currentWindow
   */
  private _onNewImage(currentWindow: number): void {
    this._breakViewOfSlice$.next(undefined);
    this._breakViewOfSlice$.complete();
    this._breakViewOfSlice$ = new Subject();
    const element = this._viewerElements[currentWindow];
    const toolStateData = cornerstoneTools.getToolState(element, 'stack')
      .data[0];
    const frames = toolStateData.imageIds;
    const currentImageIdIndex = toolStateData.currentImageIdIndex;

    if (this._mapOfViewedSlices.get(currentWindow)!.length === 0) {
      for (let index = 0; index < frames.length; index++) {
        this._mapOfViewedSlices.get(currentWindow)!.push(false);
      }
    }

    const currentImage =
      frames[
      cornerstoneTools.getToolState(element, 'stack').data[0]
        .currentImageIdIndex
      ];
    this._updateSlicerInfoAfterTimer(currentWindow, element);
    this._stateManagerProjectService.currentActiveSlice = currentImage;
    const currentActiveSliceIndex =
      this._stateManagerProjectService.currentActiveSliceIndex;
    currentActiveSliceIndex[this._getViewerId(currentWindow)] =
      currentImageIdIndex;
    this._stateManagerProjectService.currentActiveSliceIndex =
      currentActiveSliceIndex;
    const numberOfSlices = this._stateManagerProjectService.totalNumberOfSlices;
    numberOfSlices[this._getViewerId(currentWindow)] = frames.length - 1;
    this._stateManagerProjectService.totalNumberOfSlices = numberOfSlices;
    const toolName = CornerstoneTool.CustomPolylineFreehand;
    const toolState = cornerstoneTools.getToolState(element, toolName);
    if (toolState?.data.length > 0) {
      this._labelmapWasChanged(currentWindow);
      cornerstoneTools.getToolState(element, toolName).data.splice(0, 1);
    }

    if (
      this._stateManagerProjectService.isActiveShowChangesMode &&
      this._mapOfRenderedSlicesForShowChanges
        .get(this._stateManagerProjectService.currentSelectedWindow)
        ?.findIndex(
          (el) =>
            el ===
            this._stateManagerProjectService.currentActiveSliceIndex[
            this._getViewerId(
              this._stateManagerProjectService.currentSelectedWindow
            )
            ]
        ) === -1
    ) {
      this.activateShowDiffForSlice(
        this._stateManagerProjectService.currentSelectedWindow
      );
    }
  }

  private _updateSlicerInfoAfterTimer(
    currentWindow: number,
    element: HTMLElement
  ): void {
    const currentImageIdIndex = cornerstoneTools.getToolState(element, 'stack')
      .data[0].currentImageIdIndex;
    if (!this._mapOfViewedSlices.get(currentWindow)![currentImageIdIndex]) {
      timer(30)
        .pipe(takeUntil(this._breakViewOfSlice$))
        .subscribe((_) => {
          this._mapOfViewedSlices.get(currentWindow)![currentImageIdIndex] =
            true;
        });
    }
  }

  /**
   * a function called by events to track changes in the label map
   * @function _labelmapWasChanged
   */
  private _labelmapWasChanged(currentWindow: number): void {
    this._stateManagerProjectService.isMaskWasChanged = true;
    const viewerId = this._getViewerId(currentWindow);
    if (
      !this._stateManagerProjectService.isActiveShowChangesMode &&
      !this._stateManagerProjectService.isProcessOfShowDiffHide[viewerId] &&
      !this._stateManagerProjectService.isProcessOfResetDiff[viewerId]
    ) {
      if (
        this._stateManagerProjectService.hidenColors![viewerId]?.length === 0
      ) {
        if (this._stateManagerProjectService.isViewerLoaded) {
          this._stateManagerProjectService.isChangesBetweenMasks = true;
          this._stateManagerProjectService.isSaveActionDisabled = false;
        }
        const { labelmap2D } = getters.labelmap2D(
          this._viewerElements[currentWindow]
        );
        if (labelmap2D.pixelData.buffer) {
          this._changedFullPixelData.set(
            viewerId,
            this._returnNewPixelData(
              true,
              undefined,
              this._stateManagerProjectService.currentActiveSliceIndex[
              this._getViewerId(currentWindow)
              ],
              currentWindow
            )
          );
        }
      }
    }
  }

  private _getInitPropertiesForMask(labelId: number): {
    opacity: number;
    labelId: number;
  } {
    return {
      opacity: 0.2,
      labelId,
    };
  }

  private _getPixelData(volume: any): TypedArray[] {
    let aPixelData: TypedArray = volume.data;

    const spaceDirections = volume.header['space directions'];

    // first we check if direction of each axis conforms to the standard
    // if not, we transpose the volume data to conform to the standard
    if (
      !(
        spaceDirections[0][1] == 0 &&
        spaceDirections[0][2] == 0 &&
        spaceDirections[1][0] == 0 &&
        spaceDirections[1][2] == 0 &&
        spaceDirections[2][0] == 0 &&
        spaceDirections[2][1] == 0
      )
    ) {
      // normalize the space directions
      const normDirections = spaceDirections.map(
        this._pixelDataTransformationService.normalizeVector
      );

      // find the index of the maximum absolute value in each subarray
      const maxArgs =
        this._pixelDataTransformationService.argMaxAbs(normDirections);

      // define new order and signs for axial
      const newOrder = [0, 0, 0];
      const newSigns = [0, 0, 0];

      // calculate new directions and signs
      for (let i = 0; i < 3; i++) {
        newOrder[maxArgs[i]] = i;
        newSigns[maxArgs[i]] = normDirections[i][maxArgs[i]] < 0 ? -1 : 1;
      }

      // reorder the space directions using maxArgs
      volume.header['space directions'] =
        this._pixelDataTransformationService.reorder(
          volume.header['space directions'],
          maxArgs
        );

      aPixelData = this._pixelDataTransformationService.transpose(
        aPixelData,
        volume.header.sizes,
        newOrder,
        newSigns
      );

      volume.header.sizes = this._pixelDataTransformationService.reorder(
        volume.header.sizes,
        newOrder
      );
      this._viewSizes.axial = volume.header.sizes;
      volume.customOrder = true;
    }
    const ordersAndSigns =
      this._pixelDataTransformationService.getOrdersAndSigns();

    const coronalPixelData = this._pixelDataTransformationService.transpose(
      aPixelData,
      volume.header.sizes,
      ordersAndSigns.coronal.order,
      ordersAndSigns.coronal.signs
    );
    this._viewSizes.coronal = this._pixelDataTransformationService.reorder(
      this._viewSizes.axial,
      ordersAndSigns.coronal.order
    );

    const sagittalPixelData = this._pixelDataTransformationService.transpose(
      aPixelData,
      volume.header.sizes,
      ordersAndSigns.sagittal.order,
      ordersAndSigns.sagittal.signs
    );
    this._viewSizes.sagittal = this._pixelDataTransformationService.reorder(
      this._viewSizes.axial,
      ordersAndSigns.sagittal.order
    );

    return [aPixelData, coronalPixelData, sagittalPixelData];
  }

  private _getSeriesDataForAllViews(volume: {
    data: TypedArray;
    header: INrrdFileHeader;
  }): ISerieData[] {
    const spaceDirectionsProperty = 'space directions';

    const [axialPixelData, coronalPixelData, sagittalPixelData] =
      this._getPixelData(volume);

    const spaceDirections =
      this._pixelDataTransformationService.getSpaceDirections(
        volume.header[spaceDirectionsProperty]
      );

    const coronalSeriesData = larvitar.buildNrrdImage(
      {
        data: coronalPixelData,
        header: Object.assign(volume.header, {
          sizes: this._viewSizes.coronal,
          [spaceDirectionsProperty]: spaceDirections.coronal,
        }),
      },
      uuidv4(),
      {}
    );

    const sagittalSeriesData = larvitar.buildNrrdImage(
      {
        data: sagittalPixelData,
        header: Object.assign(volume.header, {
          sizes: this._viewSizes.sagittal,
          [spaceDirectionsProperty]: spaceDirections.sagittal,
        }),
      },
      uuidv4(),
      {}
    );

    const axialSeriesData = larvitar.buildNrrdImage(
      {
        data: axialPixelData,
        header: Object.assign(volume.header, {
          sizes: this._viewSizes.axial,
          [spaceDirectionsProperty]: spaceDirections.axial,
        }),
      },
      uuidv4(),
      {}
    );

    return [axialSeriesData, coronalSeriesData, sagittalSeriesData];
  }

  private _fillFullPixelData(
    coronalPixelData: TypedArray,
    axialPixelData: TypedArray,
    sagittalPixelData: TypedArray,
    isOriginalMask: boolean
  ): void {
    const targetMap = isOriginalMask
      ? this._basedFullPixelData
      : this._changedFullPixelData;
    [coronalPixelData, axialPixelData, sagittalPixelData].forEach((data, i) => {
      const index = i + 1;
      targetMap.set(
        this._getViewerId(index),
        this._returnNewPixelData(true, data, null, index)
      );
    });

    this._stateManagerProjectService.basePixelData = this._basedFullPixelData.get(this._getViewerId(ViewWindowIndex.Coronal))!;
  }

  private _subscribeToCornerstoneEvents(): void {
    Array.from(Array(3)).forEach((_, index) => {
      const i: ViewWindowIndex = index + 1;
      const element = this._viewerElements[i];
      fromEvent(element, 'cornerstonenewimage')
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe(() => this._onNewImage(i));

      fromEvent(element, 'cornersontetoolslabelmapmodified')
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe((event: any) => {
          setTimeout(() => {
            this._drawMaskForAllViews(element, i);
            if (this._isStartLoadOfMaskForViewFinish[this._getViewerId(i)]) {
              this._addEditLog(event.detail.labelmapIndex);

              this._labelmapWasChanged(ViewWindowIndex.Coronal);
              this._labelmapWasChanged(ViewWindowIndex.Axial);
              this._labelmapWasChanged(ViewWindowIndex.Sagittal);
            }
            this._isStartLoadOfMaskForViewFinish[this._getViewerId(i)] = true;
          }, 0);
        });

      this._referenceLineService.subscribeForDrawReferenceLine(
        element,
        i,
        this._viewerElements
      );
      this._mapOfRenderedSlicesForShowChanges.set(i, []);
    });
  }

  private _addEditLog(indexOfView: number): void {
    const logs = this._logsForEditEvents.get(this._getViewName(indexOfView));
    const sliceIndex =
      this._stateManagerProjectService.currentActiveSliceIndex[
      'viewer' + indexOfView
      ] + 1;
    if (logs && logs.findIndex((el) => el === sliceIndex) === -1) {
      logs!.push(sliceIndex);
      this._logsForEditEvents.set(this._getViewName(indexOfView), logs!);
    }
  }

  private _sendEditAndResetLogs(): void {
    const logsForSend: {
      caseId: string;
      userName: string;
      action: string;
      dateTime: string;
      eventOutCome: EventOutcomeLogType;
      eventType: EventTypeLogType;
    }[] = [];
    if (this._isResetCalled) {
      logsForSend.push({
        caseId: this._stateManagerProjectService.caseId,
        userName: this._stateManagerProjectService.currentUserName,
        action: LogMessageAction.CaseReseted,
        dateTime: new Date().toISOString(),
        eventOutCome: 1,
        eventType: 'UPDATE',
      });
    }

    const editSlices = Array.from(this._logsForEditEvents.values());

    if (!editSlices.every((el) => el.length === 0)) {
      let action = '';
      editSlices.forEach((slices, i) => {
        action =
          action +
          `${this._getViewName(i + 1)} slice #<${slices.length === 0
            ? ''
            : slices.length > 1
              ? slices.join(',')
              : slices[0]
          }>. `;
      });
      logsForSend.push({
        caseId: this._stateManagerProjectService.caseId,
        userName: this._stateManagerProjectService.currentUserName,
        action: action,
        dateTime: new Date().toISOString(),
        eventOutCome: 1,
        eventType: 'UPDATE',
      });
    }

    logsForSend.push({
      caseId: this._stateManagerProjectService.caseId,
      userName: this._stateManagerProjectService.currentUserName,
      action: LogMessageAction.CaseSaved,
      dateTime: new Date().toISOString(),
      eventOutCome: 1,
      eventType: 'UPDATE',
    });

    const requests: Observable<any>[] = [];
    logsForSend.forEach((log) =>
      requests.push(
        this._projectActionsRequestService.requestSaveLogForCase(
          log.caseId,
          log.userName,
          log.action,
          log.dateTime,
          log.eventOutCome,
          log.eventType
        )
      )
    );

    concat(...requests).subscribe(() => {
      this._logsForEditEvents = new Map<string, number[]>();
      this._isResetCalled = false;
      for (let i = 1; i < 4; i++) {
        this._logsForEditEvents.set(this._getViewName(i), []);
      }
    });
  }

  private _addCustomTools(): void {
    [
      EraseSquareBrushTool,
      EraseCircleBrushTool,
      CircleBrushTool,
      SquareBrushTool,
      CustomPolylineFreehandTool,
      ShowDiffTool,
      CustomZoomMouseWheelTool,
      EmptyTool,
    ].forEach((tool) => cornerstoneTools.addTool(tool));
  }

  private _maskOpacitySetters(value: number, index: number): void {
    try {
      const properties = {
        opacity: value / 100,
        labelId: index,
      };
      const lut = getters.colorLUT(properties.labelId);
      
      if (lut) {
        lut.forEach((color, index) => {
          if (index !== 0) {
            color[3] = Math.round(255 * (value / 100));
          }
        });
        lut.shift();
        setters.colorLUT(properties.labelId, lut);
        const element = this._viewerElements[index];

        if (!element.getAttribute('style')) {
          throw "error on element style";
        }

        if (element) {
          this.activeLabel = this._segmentationService.fetchSegmentIndex();
          setters.activeSegmentIndex(element, this.activeLabel);
          setters.activeLabelmapIndex(element, index);
          setters.colorLUTIndexForLabelmap3D(
            getters.labelmap3D(element, index),
            index
          );
        }
        this._stateManagerProjectService.currentActiveColor = this.activeLabel;
      }
    } catch (error) {
      this.router.navigate(['/activecase']);
    }
  }

  private _fillViewSizes(volume): void {
    const index = volume.header.kinds[0] == 'domain' ? 0 : 1;
    const cols = volume.header.sizes[index]; // x
    const rows = volume.header.sizes[index + 1]; // y
    const frames = volume.header.sizes[index + 2]; // z
    this._viewSizes = {
      axial: [cols, rows, frames],
      sagittal: [rows, frames, cols],
      coronal: [cols, frames, rows],
    };
  }

  private _createLabelmap2d(labelmap3d, index: number, color: number): void {
    if (labelmap3d.labelmaps2D[index]) {
      return;
    }

    labelmap3d.labelmaps2D[index] = {
      ...labelmap3d.labelmaps2D.find((x) => x),
    };

    const labelmap2D = labelmap3d.labelmaps2D[index];
    labelmap2D.pixelData = labelmap2D.pixelData.map((_) => 0);
    labelmap2D.segmentsOnLabelmap = [0];
    color !== 0 && labelmap2D.segmentsOnLabelmap.push(color);
  }

  private _calcCoordinatesForLabelmap3d(
    index: number,
    cols: number,
    z: number
  ): IVector {
    const x = index % cols;
    return {
      x,
      y: (index - x) / cols,
      z,
    };
  }

  private _drawMaskFromOneViewToAnother(
    pixelDataDiff: number[][],
    currentImageIdIndex: number,
    sizes: { axial: number[]; coronal: number[]; sagittal: number[] },
    firstLabelmap3D,
    secondLabelmap3D,
    main: string,
    first: string,
    second: string
  ): void {
    const orders = this._pixelDataTransformationService.getOrdersAndSigns();
    const transformMainFirst =
      this._pixelDataTransformationService.getTransformOrder(
        [orders[main].order, orders[main].signs],
        [orders[first].order, orders[first].signs]
      );
    const transformMainSecond =
      this._pixelDataTransformationService.getTransformOrder(
        [orders[main].order, orders[main].signs],
        [orders[second].order, orders[second].signs]
      );

    const undo = pixelDataDiff.reduce(
      (
        acc: {
          [key: string]: { [key: number]: number[][] };
        },
        diff
      ) => {
        const index = diff[0];
        const color = diff[2];

        const mainPoint = [
          ...this._pixelDataTransformationService.getPointInSlice(
            index,
            sizes[main]
          ),
          currentImageIdIndex,
        ];
        // calculate first point
        const firstPoint = this._pixelDataTransformationService.transposePoint(
          mainPoint,
          sizes[main],
          transformMainFirst[0],
          transformMainFirst[1]
        );
        const firstIndex = this._pixelDataTransformationService.getIdxInSlice(
          firstPoint,
          sizes[first]
        );
        this._createLabelmap2d(firstLabelmap3D, firstPoint[2], color);
        firstLabelmap3D.labelmaps2D[firstPoint[2]].pixelData[firstIndex] =
          color;

        this._addDiffToCustomObj(
          acc[first],
          firstPoint[2],
          firstIndex,
          diff[1],
          color
        );
        // calculate second point
        const secondPoint = this._pixelDataTransformationService.transposePoint(
          mainPoint,
          sizes[main],
          transformMainSecond[0],
          transformMainSecond[1]
        );

        const secondIndex = this._pixelDataTransformationService.getIdxInSlice(
          secondPoint,
          sizes[second]
        );

        this._createLabelmap2d(secondLabelmap3D, secondPoint[2], color);
        secondLabelmap3D.labelmaps2D[secondPoint[2]].pixelData[secondIndex] =
          color;
        this._addDiffToCustomObj(
          acc[second],
          secondPoint[2],
          secondIndex,
          diff[1],
          color
        );

        return acc;
      },
      { [first]: {}, [second]: {} }
    );

    this._addDiffToLabelmap3D(firstLabelmap3D, undo[first]);
    this._addDiffToLabelmap3D(secondLabelmap3D, undo[second]);
  }

  private _drawMaskForAllViews(
    element: HTMLElement,
    viewWindowIndex: ViewWindowIndex
  ): void {
    const { labelmap3D, currentImageIdIndex } = getters.labelmap2D(element);
    const { coronalLabelmap3D, axialLabelmap3D, sagittalLabelmap3D } =
      this._getAllElemsAndLabelmaps3D();

    if (
      !labelmap3D.undo.length ||
      new Set([
        coronalLabelmap3D.undo.length,
        axialLabelmap3D.undo.length,
        sagittalLabelmap3D.undo.length,
      ]).size === 1
    ) {
      return;
    }

    const [coronalCols, coronalRows, coronalFrames] = this._viewSizes.coronal;
    const [axialCols, axialRows, axialFrames] = this._viewSizes.axial;
    const [sagittalCols, sagittalRows, sagittalFrames] =
      this._viewSizes.sagittal;

    const sizes = {
      coronalCols,
      coronalRows,
      coronalFrames,
      axialCols,
      axialRows,
      axialFrames,
      sagittalCols,
      sagittalRows,
      sagittalFrames,
    };

    const views = {
      axial: 'axial',
      coronal: 'coronal',
      sagittal: 'sagittal',
    };

    switch (viewWindowIndex) {
      case ViewWindowIndex.Coronal:
        this._drawMaskFromOneViewToAnother(
          labelmap3D.undo[labelmap3D.undo.length - 1][0].diff,
          currentImageIdIndex,
          this._viewSizes,
          axialLabelmap3D,
          sagittalLabelmap3D,
          views.coronal,
          views.axial,
          views.sagittal
        );

        break;
      case ViewWindowIndex.Axial:
        this._drawMaskFromOneViewToAnother(
          labelmap3D.undo[labelmap3D.undo.length - 1][0].diff,
          currentImageIdIndex,
          this._viewSizes,
          coronalLabelmap3D,
          sagittalLabelmap3D,
          views.axial,
          views.coronal,
          views.sagittal
        );

        break;
      case ViewWindowIndex.Sagittal:
        this._drawMaskFromOneViewToAnother(
          labelmap3D.undo[labelmap3D.undo.length - 1][0].diff,
          currentImageIdIndex,
          this._viewSizes,
          coronalLabelmap3D,
          axialLabelmap3D,
          views.sagittal,
          views.coronal,
          views.axial
        );

        break;
    }
    this._stateManagerProjectService.is3dUpdateNeeded = true;
  }

  private _fillSegmentationColors(): void {
    const element = this._viewerElements[ViewWindowIndex.Coronal];
    const labelmap3D = getters.labelmap2D(element).labelmap3D;

    const hiddenColors: boolean[] = [];
    for (let i = 0; i < 10; i++) {
      hiddenColors.push(false);
    }
    labelmap3D.segmentsHidden = hiddenColors;

    const usedSegmentationNumbers: number[] = [];

    for (const index of this._basedFullPixelData.get(
      this._getViewerId(ViewWindowIndex.Coronal)
    )!) {
      if (
        index !== 0 &&
        usedSegmentationNumbers.findIndex(
          (usedSegment: number) => usedSegment === index
        ) === -1
      ) {
        usedSegmentationNumbers.push(index);
      }
    }

    const colorData: {
      viewer1: IColorData[];
      viewer2: IColorData[];
      viewer3: IColorData[];
    } = {
      viewer1: [],
      viewer2: [],
      viewer3: [],
    };
    colorData.viewer1 = usedSegmentationNumbers.map((index: number) => {
      return {
        indexOfSegment: index,
        color: larvitar.rgbToHex(
          getters.colorForSegmentIndexColorLUT(
            getters.labelmap3D(element, 1).colorLUTIndex,
            index
          )
        ),
        state: true,
      };
    });
    colorData.viewer2 = colorData.viewer1;
    colorData.viewer3 = colorData.viewer1;
    this._stateManagerProjectService.colorsData = colorData;
  }

  private _addDiffToCustomObj(
    view: { [key: number]: number[][] },
    frame: number,
    pixelIndex: number,
    colorOld: number,
    colorNew: number
  ): void {
    !view[frame] && (view[frame] = []);
    view[frame].push([pixelIndex, colorOld, colorNew]);
  }

  private _addDiffToLabelmap3D(
    labelmap3D,
    undo: { [key: number]: number[][] }
  ): void {
    labelmap3D.undo.push(
      Object.keys(undo).map((key) => ({
        imageIdIndex: key,
        diff: undo[key],
      }))
    );

    labelmap3D.redo.length = 0;
  }

  private _getAllElemsAndLabelmaps3D() {
    const coronalElem = this._viewerElements[ViewWindowIndex.Coronal];
    const axialElem = this._viewerElements[ViewWindowIndex.Axial];
    const sagittalElem = this._viewerElements[ViewWindowIndex.Sagittal];
    return {
      coronalElem,
      coronalLabelmap3D: getters.labelmap3D(
        coronalElem,
        ViewWindowIndex.Coronal
      ),
      axialElem,
      axialLabelmap3D: getters.labelmap3D(axialElem, ViewWindowIndex.Axial),
      sagittalElem,
      sagittalLabelmap3D: getters.labelmap3D(
        sagittalElem,
        ViewWindowIndex.Sagittal
      ),
    };
  }

  private _setTotalNumberOfSlices(): void {
    const totalSlices = this._stateManagerProjectService.totalNumberOfSlices;
    if (
      totalSlices[this._getViewerId(ViewWindowIndex.Coronal)] === 0 ||
      totalSlices[this._getViewerId(ViewWindowIndex.Axial)] === 0 ||
      totalSlices[this._getViewerId(ViewWindowIndex.Sagittal)] === 0
    ) {
      for (let i = 1; i < 4; i++) {
        const toolStateData = cornerstoneTools.getToolState(
          this._viewerElements[i],
          'stack'
        ).data[0];
        const frames = toolStateData.imageIds;
        totalSlices[this._getViewerId(i)] = frames.length;
      }
      this._stateManagerProjectService.totalNumberOfSlices = totalSlices;
    }
  }

  private _setMiddleSlices() {
    for (let i = 1; i < 4; i++) {
      const middleSlice = Math.floor(
        (this._stateManagerProjectService.totalNumberOfSlices[
          this._getViewerId(i)
        ] -
          1) /
        2
      );
      this.setIndexOfSlider(middleSlice, i);
    }
  }

  private _getViewerId(index: ViewWindowIndex): string {
    return `viewer${index}`;
  }

  private _getViewName(index: ViewWindowIndex): string {
    const views = {
      1: 'coronal',
      2: 'axial',
      3: 'sagittal',
    };
    switch (index) {
      case ViewWindowIndex.Axial:
      case ViewWindowIndex.Coronal:
      case ViewWindowIndex.Sagittal: {
        return views[index];
      }
      default: {
        return '';
      }
    }
  }

  _closeOpenedMenu() {
    this.closeOpenedMenu.next();
  }
}
